import React, { useState } from 'react';
import { FaEnvelope, FaUser, FaBuilding, FaPaperclip, FaPaperPlane, FaCheckCircle, FaExclamationCircle, FaTimes, FaSpinner } from 'react-icons/fa';
import { submitContactForm } from '../api/client';
import SimpleCaptcha from './SimpleCaptcha';
import SEO from './SEO';
import InternalLinks from './InternalLinks';
import BASE_URL, { SEO_BASE_URL } from '../config';

const ContactUs = ({ onNavigate }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    requestType: '',
    description: '',
    attachment: null
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaKey, setCaptchaKey] = useState(0);
  const [honeypot, setHoneypot] = useState(''); // Bot trap field

  const requestTypes = [
    'Website Issue',
    'Feature Request',
    'Subscription Inquiry',
    'Suggestion',
    'Other'
  ];

  const allowedFileTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/bmp', 'application/pdf'];
  const maxFileSize = 5 * 1024 * 1024; // 5MB

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    // Honeypot check - if filled, silently reject (bot detected)
    if (honeypot) {
      console.log('Bot detected via honeypot');
      return false;
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.requestType) {
      newErrors.requestType = 'Please select a request type';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    } else if (formData.description.length > 5000) {
      newErrors.description = 'Description must not exceed 5000 characters';
    }

    if (!captchaToken || !captchaAnswer) {
      newErrors.captcha = 'Please solve the security question';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCaptchaVerify = (token, answer) => {
    setCaptchaToken(token);
    setCaptchaAnswer(answer);
    // Clear captcha error when user provides an answer
    if (answer) {
      setErrors(prev => prev.captcha ? { ...prev, captcha: '' } : prev);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!allowedFileTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, attachment: 'Invalid file type. Allowed: PNG, JPG, JPEG, GIF, BMP, PDF' }));
        return;
      }

      // Validate file size
      if (file.size > maxFileSize) {
        setErrors(prev => ({ ...prev, attachment: 'File size exceeds 5MB limit' }));
        return;
      }

      setFormData(prev => ({ ...prev, attachment: file }));
      setErrors(prev => ({ ...prev, attachment: '' }));

      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setAttachmentPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setAttachmentPreview(null);
      }
    }
  };

  const removeAttachment = () => {
    setFormData(prev => ({ ...prev, attachment: null }));
    setAttachmentPreview(null);
    // Reset the file input
    const fileInput = document.getElementById('attachment');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await submitContactForm(formData, captchaToken, captchaAnswer);
      setSubmitStatus({ type: 'success', message: response.message });
      // Reset form on success
      setFormData({
        firstName: '',
        lastName: '',
        companyName: '',
        email: '',
        requestType: '',
        description: '',
        attachment: null
      });
      setAttachmentPreview(null);
      const fileInput = document.getElementById('attachment');
      if (fileInput) fileInput.value = '';
      // Reset captcha
      setCaptchaToken('');
      setCaptchaAnswer('');
      setCaptchaKey(prev => prev + 1);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'An error occurred. Please try again later.';
      setSubmitStatus({ type: 'error', message: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact Us"
        description="Get in touch with the ItinerarySeek team. We're here to help with questions about AI travel planning, feature requests, or any support you need."
        canonical={`${SEO_BASE_URL}/contact`}
      />
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary-600 to-secondary-500 bg-clip-text text-transparent mb-4">
            Contact Us
          </h1>
          <p className="text-neutral-600 text-lg">
            Have a question or feedback? We'd love to hear from you!
          </p>
          <p className="text-neutral-500 text-sm mt-4">
            Before reaching out, you might find your answer in our{' '}
            <a href="/faq" onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('faq'); }} className="text-primary-600 hover:underline font-medium">FAQ</a>
            {' '}or{' '}
            <a href="/howto" onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('howto'); }} className="text-primary-600 hover:underline font-medium">How-To guides</a>.
          </p>
        </div>

        {/* Success/Error Message */}
        {submitStatus && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
            submitStatus.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            {submitStatus.type === 'success' ? (
              <FaCheckCircle className="text-green-500 text-xl flex-shrink-0" />
            ) : (
              <FaExclamationCircle className="text-red-500 text-xl flex-shrink-0" />
            )}
            <span>{submitStatus.message}</span>
            <button
              onClick={() => setSubmitStatus(null)}
              className="ml-auto text-neutral-500 hover:text-neutral-700"
            >
              <FaTimes />
            </button>
          </div>
        )}

        {/* Contact Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8">
          {/* Honeypot field - hidden from users, visible to bots */}
          <input
            type="text"
            name="website"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            autoComplete="off"
            tabIndex="-1"
            aria-hidden="true"
            style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
          />

          {/* Name Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                First Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="John"
                  className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:ring-4 focus:ring-primary-100 outline-none focus:border-primary-500 transition-all ${
                    errors.firstName ? 'border-red-500' : 'border-neutral-300'
                  }`}
                />
              </div>
              {errors.firstName && (
                <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Last Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Doe"
                  className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:ring-4 focus:ring-primary-100 outline-none focus:border-primary-500 transition-all ${
                    errors.lastName ? 'border-red-500' : 'border-neutral-300'
                  }`}
                />
              </div>
              {errors.lastName && (
                <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
              )}
            </div>
          </div>

          {/* Company Name (Optional) */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Company Name <span className="text-neutral-500">(Optional)</span>
            </label>
            <div className="relative">
              <FaBuilding className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                placeholder="Your Company"
                className="w-full pl-10 pr-4 py-3 border-2 border-neutral-300 rounded-lg focus:ring-4 focus:ring-primary-100 outline-none focus:border-primary-500 transition-all"
              />
            </div>
          </div>

          {/* Email */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="john.doe@example.com"
                className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:ring-4 focus:ring-primary-100 outline-none focus:border-primary-500 transition-all ${
                  errors.email ? 'border-red-500' : 'border-neutral-300'
                }`}
              />
            </div>
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          {/* Request Type */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Type of Request <span className="text-red-500">*</span>
            </label>
            <select
              name="requestType"
              value={formData.requestType}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-4 focus:ring-primary-100 outline-none focus:border-primary-500 transition-all appearance-none bg-white ${
                errors.requestType ? 'border-red-500' : 'border-neutral-300'
              }`}
            >
              <option value="">Select a request type</option>
              {requestTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            {errors.requestType && (
              <p className="text-red-500 text-sm mt-1">{errors.requestType}</p>
            )}
          </div>

          {/* Description */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={5}
              placeholder="Please describe your request in detail..."
              className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-4 focus:ring-primary-100 outline-none focus:border-primary-500 transition-all resize-none ${
                errors.description ? 'border-red-500' : 'border-neutral-300'
              }`}
            />
            <div className="flex justify-between mt-1">
              {errors.description ? (
                <p className="text-red-500 text-sm">{errors.description}</p>
              ) : (
                <span></span>
              )}
              <p className={`text-sm ${formData.description.length > 5000 ? 'text-red-500' : 'text-neutral-500'}`}>
                {formData.description.length}/5000
              </p>
            </div>
          </div>

          {/* Attachment */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Attachment <span className="text-neutral-500">(Optional)</span>
            </label>
            <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-all ${
              errors.attachment ? 'border-red-300 bg-red-50' : 'border-neutral-300 hover:border-primary-400 hover:bg-primary-50'
            }`}>
              {formData.attachment ? (
                <div className="flex items-center justify-center gap-4">
                  {attachmentPreview ? (
                    <img src={attachmentPreview} alt="Preview" className="h-16 w-16 object-cover rounded" />
                  ) : (
                    <FaPaperclip className="text-3xl text-primary-500" />
                  )}
                  <div className="text-left">
                    <p className="font-medium text-neutral-700">{formData.attachment.name}</p>
                    <p className="text-sm text-neutral-500">
                      {(formData.attachment.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={removeAttachment}
                    className="ml-4 p-2 text-red-500 hover:bg-red-100 rounded-full transition-colors"
                  >
                    <FaTimes />
                  </button>
                </div>
              ) : (
                <>
                  <input
                    type="file"
                    id="attachment"
                    onChange={handleFileChange}
                    accept=".png,.jpg,.jpeg,.gif,.bmp,.pdf"
                    className="hidden"
                  />
                  <label htmlFor="attachment" className="cursor-pointer">
                    <FaPaperclip className="text-3xl text-neutral-500 mx-auto mb-2" />
                    <p className="text-neutral-600 font-medium">Click to upload</p>
                    <p className="text-sm text-neutral-500 mt-1">
                      PNG, JPG, JPEG, GIF, BMP, PDF (Max 5MB)
                    </p>
                  </label>
                </>
              )}
            </div>
            {errors.attachment && (
              <p className="text-red-500 text-sm mt-1">{errors.attachment}</p>
            )}
          </div>

          {/* CAPTCHA */}
          <div className="mb-8">
            <SimpleCaptcha
              key={captchaKey}
              onVerify={handleCaptchaVerify}
              disabled={isSubmitting}
            />
            {errors.captcha && (
              <p className="text-red-600 text-sm mt-2">{errors.captcha}</p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-4 rounded-xl font-semibold text-white transition-all flex items-center justify-center gap-2 ${
              isSubmitting
                ? 'bg-neutral-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 hover:shadow-lg'
            }`}
          >
            {isSubmitting ? (
              <>
                <FaSpinner className="animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <FaPaperPlane />
                Send Message
              </>
            )}
          </button>
        </form>

        {/* Contact Info */}
        <div className="mt-8 text-center text-neutral-600">
          <p>We typically respond within 24-48 hours.</p>
        </div>

        {/* Internal Links for SEO */}
        <InternalLinks currentPage="contact" onNavigate={onNavigate} variant="compact" />
        </div>
      </div>
    </>
  );
};

export default ContactUs;
